import { getCommonBound, type IBound } from '@blocksuite/global/utils';
import { getEdgelessService } from '@pique/core/blocksuite/presets/ai/utils/selection-utils';
import { useActiveBlocksuiteEditor } from '@pique/core/hooks/use-block-suite-editor';
import { useLiveData, useService } from '@toeverything/infra';
import type { HTMLAttributes } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const TriangleLeftIcon = () => (
  <svg
    style={{
      width: 'max(22px, min(28px, 3.5vh))',
      height: 'max(22px, min(28px, 3.5vh))',
    }}
    viewBox="0 0 20 32"
    fill="var(--affine-brand-color)"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M15 7C15.5 7 16 7.3 16 8V24C16 24.7 15.5 25 15 25C14.7 25 14.4 24.9 14.1 24.6L6.5 17.2C5.8 16.5 5.8 15.5 6.5 14.8L14.1 7.4C14.4 7.1 14.7 7 15 7Z" />
  </svg>
);

const TriangleRightIcon = () => (
  <svg
    style={{
      width: 'max(22px, min(28px, 3.5vh))',
      height: 'max(22px, min(28px, 3.5vh))',
    }}
    viewBox="0 0 20 32"
    fill="var(--affine-brand-color)"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 7C4.5 7 4 7.3 4 8V24C4 24.7 4.5 25 5 25C5.3 25 5.6 24.9 5.9 24.6L13.5 17.2C14.2 16.5 14.2 15.5 13.5 14.8L5.9 7.4C5.6 7.1 5.3 7 5 7Z" />
  </svg>
);

import { WorkbenchService } from '../../services/workbench';
import * as styles from './split-view.css';

interface ResizeHandleProps extends HTMLAttributes<HTMLDivElement> {
  resizing: boolean;
  onResizeStart: () => void;
  onResizeEnd: () => void;
  onResizing: (offset: { x: number; y: number }) => void;
}

export const ResizeHandle = ({
  resizing,
  onResizing,
  onResizeStart,
  onResizeEnd,
}: ResizeHandleProps) => {
  const workbench = useService(WorkbenchService).workbench;
  const views = useLiveData(workbench.views$);
  const [editorContainer] = useActiveBlocksuiteEditor();

  const firstViewSize = useLiveData(views[0]?.size$ ?? null) ?? 50;
  const secondViewSize = useLiveData(views[1]?.size$ ?? null) ?? 50;

  const isLeftExpanded = firstViewSize >= 95;
  const isRightExpanded = secondViewSize >= 95;
  const isFullscreen = isLeftExpanded || isRightExpanded;

  const activeListenersRef = useRef<{
    onMouseMove: ((e: MouseEvent) => void) | null;
    onMouseUp: ((e: MouseEvent) => void) | null;
  }>({ onMouseMove: null, onMouseUp: null });

  useEffect(() => {
    return () => {
      if (activeListenersRef.current.onMouseMove) {
        document.removeEventListener(
          'mousemove',
          activeListenersRef.current.onMouseMove
        );
      }
      if (activeListenersRef.current.onMouseUp) {
        document.removeEventListener(
          'mouseup',
          activeListenersRef.current.onMouseUp
        );
      }
      activeListenersRef.current = { onMouseMove: null, onMouseUp: null };
    };
  }, []);

  const centerEdgelessViewport = useCallback(() => {
    if (!editorContainer?.host) return;

    const edgelessRootService = getEdgelessService(editorContainer.host);

    if (!edgelessRootService?.elements) return;

    const elementBounds = edgelessRootService.elements
      .map((element: { elementBound?: IBound }) => element?.elementBound)
      .filter((bound): bound is IBound => Boolean(bound));

    if (elementBounds.length > 0) {
      const overallBound = getCommonBound(elementBounds);

      if (overallBound && edgelessRootService.viewport) {
        edgelessRootService.viewport.setViewportByBound(
          overallBound,
          [20, 20, 20, 20],
          true
        );
      }
    }
  }, [editorContainer]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isFullscreen) return;
      e.preventDefault();

      onResizeStart();
      const prevPos = { x: e.clientX, y: e.clientY };

      const onMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        const dx = e.clientX - prevPos.x;
        const dy = e.clientY - prevPos.y;
        onResizing({ x: dx, y: dy });
        prevPos.x = e.clientX;
        prevPos.y = e.clientY;
      };

      const onMouseUp = (e: MouseEvent) => {
        e.preventDefault();
        onResizeEnd();
        document.removeEventListener('mousemove', onMouseMove);
        activeListenersRef.current.onMouseMove = null;
        activeListenersRef.current.onMouseUp = null;
      };

      activeListenersRef.current.onMouseMove = onMouseMove;
      activeListenersRef.current.onMouseUp = onMouseUp;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp, { once: true });
    },
    [isFullscreen, onResizeEnd, onResizeStart, onResizing]
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isFullscreen) return;
      const touch = e.touches[0];
      if (!touch) return;

      onResizeStart();
      const prevPos = { x: touch.clientX, y: touch.clientY };

      const onTouchMove = (e: TouchEvent) => {
        const t = e.touches[0];
        if (!t) return;
        const dx = t.clientX - prevPos.x;
        const dy = t.clientY - prevPos.y;
        onResizing({ x: dx, y: dy });
        prevPos.x = t.clientX;
        prevPos.y = t.clientY;
      };

      const onTouchEnd = () => {
        onResizeEnd();
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
      };

      document.addEventListener('touchmove', onTouchMove, { passive: true });
      document.addEventListener('touchend', onTouchEnd, { once: true });
    },
    [isFullscreen, onResizeEnd, onResizeStart, onResizing]
  );

  const handleExpandLeft = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      workbench.setFirstViewToLarge(100, 0);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(centerEdgelessViewport, { timeout: 600 });
      } else {
        setTimeout(centerEdgelessViewport, 450);
      }
    },
    [workbench, centerEdgelessViewport]
  );

  const handleExpandRight = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      workbench.setFirstViewToLarge(0, 100);
    },
    [workbench]
  );

  const handleRestore = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      workbench.setFirstViewToLarge(50, 50);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(centerEdgelessViewport, { timeout: 600 });
      } else {
        setTimeout(centerEdgelessViewport, 450);
      }
    },
    [workbench, centerEdgelessViewport]
  );

  const content = useMemo(() => {
    if (isLeftExpanded) {
      return (
        <button
          className={styles.expandButton}
          onMouseDown={e => e.stopPropagation()}
          onClick={handleRestore}
          aria-label="Restore split view"
        >
          <TriangleLeftIcon />
        </button>
      );
    }

    if (isRightExpanded) {
      return (
        <button
          className={styles.expandButton}
          onMouseDown={e => e.stopPropagation()}
          onClick={handleRestore}
          aria-label="Restore split view"
        >
          <TriangleRightIcon />
        </button>
      );
    }

    return (
      <>
        <button
          className={styles.expandButton}
          onMouseDown={e => e.stopPropagation()}
          onClick={handleExpandRight}
          aria-label="Expand right view"
        >
          <TriangleLeftIcon />
        </button>
        <button
          className={styles.expandButton}
          onMouseDown={e => e.stopPropagation()}
          onClick={handleExpandLeft}
          aria-label="Expand left view"
        >
          <TriangleRightIcon />
        </button>
      </>
    );
  }, [
    isLeftExpanded,
    isRightExpanded,
    handleRestore,
    handleExpandRight,
    handleExpandLeft,
  ]);

  return (
    <div
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      data-resizing={resizing || null}
      data-fullscreen={isFullscreen || null}
      data-left-expanded={isLeftExpanded || null}
      data-right-expanded={isRightExpanded || null}
      className={styles.resizeHandle}
    >
      <div className={styles.resizeHandleHoverZone}>
        <div className={styles.resizeHandleInner}>{content}</div>
      </div>
    </div>
  );
};
